import { currencyCode } from "$root/utils/currency";
import type { Currencies } from "@eu.mdotm.api/portfolio-lab";
import { useLocaleFormatters } from "$root/localization/hooks";
import { type TextInputProps, TextInput } from "@mdotm/shadcn/mdotm/components";
import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { mapCaretAcrossFormat } from "./caret";
import {
	type MonetarySeparators,
	canonicalize,
	expandSuffix,
	groupFormat,
	isMalformedAmount,
	localeSeparators,
	normalizePastedAmount,
	numberToCanonical,
} from "./format";

export type MonetaryInputProps = Omit<
	TextInputProps,
	| "type"
	| "inputMode"
	| "value"
	| "onChange"
	| "min"
	| "max"
	| "invalid"
	| "innerRef"
	| "rightContent"
	| "inputAppearance"
	| "onChangeText"
	| "autoTrimOnBlur"
> & {
	/** Canonical numeric value backing the field; null when the field is empty. */
	value: number | null;
	/**
	 * Emits the canonical number (never the formatted display string); null when the field is empty,
	 * NaN while the text is not one amount (see {@link isMalformedAmount}).
	 */
	onChange(value: number | null): void;
	/** Currency whose ISO code is the field adornment; the platform default when unset. */
	currency?: Currencies | null;
	/** Renders the currency adornment; off when the currency is shown beside the field instead. */
	showCurrencyCode?: boolean;
	min?: number;
	max?: number;
	/** Extra invalid state driven by the consumer; combined with the built-in min/max range check. */
	invalid?: boolean;
	/** Decimals the field accepts: 2 (the default) for cents, 0 for integer-only direct entry. */
	fractionDigits?: number;
};

/**
 * Parses the canonical string the field emits. With `fractionDigits` 0 a typed fractional part is
 * truncated away (it exists in the field only as a path to a scale suffix, which expands to an
 * integer before reaching here); otherwise the string already carries at most `fractionDigits`
 * decimals and is parsed as is, so the number is the nearest double to exactly what was typed.
 */
function canonicalToNumber(canonical: string, fractionDigits: number): number | null {
	if (!/\d/.test(canonical)) {
		return null;
	}
	return fractionDigits > 0 ? Number(canonical) : Math.trunc(Number(canonical));
}

/**
 * The text an outside `value` displays as: grouped, verbatim when integer-only, otherwise truncated
 * to `fractionDigits` decimals; empty for null or a non-finite value.
 */
function valueToDisplay(value: number | null, fractionDigits: number, separators: MonetarySeparators): string {
	if (value == null || !Number.isFinite(value)) {
		return "";
	}
	return groupFormat(fractionDigits > 0 ? numberToCanonical(value, fractionDigits) : String(value), separators);
}

/**
 * Locale-parametric monetary input over `mdotm/TextInput`: live thousands grouping with
 * caret preservation, k/m/b/t suffix expansion, and a currency-code adornment. Pasted text may carry
 * a currency code or symbol and foreign separators (see {@link normalizePastedAmount}).
 *
 * Direct entry accepts up to `fractionDigits` decimals and emits the number parsed from exactly
 * the typed digits; with 0 it is integer-only. Text that is not one amount — a second decimal
 * separator, or more decimals than accepted — is never rewritten: it stays on screen as typed,
 * flagged invalid, and emits NaN, so no typed digit is lost. Suffix expansion yields an integer
 * either way.
 *
 * The display is a string kept in local state so transient typing states (a trailing decimal
 * separator, fractional digits on the way to a suffix) survive the reformat; the canonical
 * `value` prop re-derives the display only when it changes from OUTSIDE (not from our own
 * `onChange` echo). The state is wrapped in an object so every keystroke re-renders — the
 * caret restore must run even when reformatting yields an unchanged string (e.g. a stripped
 * junk character).
 */
export function MonetaryInput({
	value,
	onChange,
	currency,
	showCurrencyCode = true,
	min = 0,
	max,
	invalid = false,
	fractionDigits = 2,
	...inputProps
}: MonetaryInputProps): React.JSX.Element {
	const { locale } = useLocaleFormatters();
	const separators = useMemo(() => localeSeparators(locale), [locale]);

	const [display, setDisplay] = useState(() => ({
		text: valueToDisplay(value, fractionDigits, separators),
		malformed: false,
	}));
	const inputRef = useRef<HTMLInputElement | null>(null);
	const pendingCaret = useRef<number | null>(null);
	const lastValue = useRef(value);

	// Object.is, so the NaN this field emits for malformed text is recognised as its own echo.
	if (!Object.is(value, lastValue.current)) {
		lastValue.current = value;
		setDisplay({ text: valueToDisplay(value, fractionDigits, separators), malformed: false });
	}

	// Runs after every commit on purpose: a pending caret must be applied on whichever render
	// followed the keystroke, including ones where the formatted text came out unchanged.
	useLayoutEffect(() => {
		if (pendingCaret.current != null && inputRef.current) {
			inputRef.current.setSelectionRange(pendingCaret.current, pendingCaret.current);
			pendingCaret.current = null;
		}
	});

	const outOfRange = value != null && (value < min || (max != null && value > max));

	return (
		<TextInput
			{...inputProps}
			type="text"
			inputMode={fractionDigits > 0 ? "decimal" : "numeric"}
			value={display.text}
			invalid={invalid || outOfRange || display.malformed}
			rightContent={showCurrencyCode ? currencyCode(currency) : undefined}
			// type="text" doesn't auto-right-align like type="number" does — style it manually.
			inputAppearance={{ className: "text-right tabular-nums" }}
			innerRef={inputRef}
			onChange={(e) => {
				const raw = e.target.value;
				const rawCaret = e.target.selectionStart ?? raw.length;
				const normalized = normalizePastedAmount(raw, separators);
				const expanded = expandSuffix(normalized, separators);
				// Integer-only mode keeps any typed fraction: it is the path to a scale suffix.
				if (expanded == null && fractionDigits > 0 && isMalformedAmount(normalized, separators, fractionDigits)) {
					pendingCaret.current = rawCaret;
					setDisplay({ text: raw, malformed: true });
					lastValue.current = Number.NaN;
					onChange(Number.NaN);
					return;
				}
				const canonical = expanded ?? canonicalize(normalized, separators);
				const formatted = groupFormat(canonical, separators);
				pendingCaret.current =
					expanded != null ? formatted.length : mapCaretAcrossFormat(raw, rawCaret, formatted, separators);
				setDisplay({ text: formatted, malformed: false });
				const emitted = canonicalToNumber(canonical, fractionDigits);
				lastValue.current = emitted;
				onChange(emitted);
			}}
		/>
	);
}
